import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  TrendingUp, TrendingDown, DollarSign, BarChart3, ArrowUpRight,
  ArrowDownRight, RefreshCw, Wallet, LineChart, History,
  Trophy, ChevronDown, Loader2, AlertTriangle, RotateCcw
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatUSD(v) {
  if (v == null) return '$0.00';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(v);
}

function formatCompact(v) {
  if (!v) return '$0';
  if (v >= 1e9) return `$${(v / 1e9).toFixed(1)}B`;
  if (v >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `$${(v / 1e3).toFixed(1)}K`;
  return `$${v.toFixed(2)}`;
}

function PctBadge({ value }) {
  if (value == null) return null;
  const positive = value >= 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-medium ${positive ? 'text-emerald-400' : 'text-red-400'}`}>
      {positive ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
      {Math.abs(value).toFixed(2)}%
    </span>
  );
}


// ---- Asset List Panel ----
function AssetPanel({ assets, selectedAsset, onSelect, loading }) {
  return (
    <div className="bg-[#111111] border border-white/10 rounded-lg overflow-hidden" data-testid="asset-panel">
      <div className="p-4 border-b border-white/10">
        <h3 className="font-manrope font-semibold text-white text-sm flex items-center gap-2">
          <BarChart3 size={14} className="text-[#C5A059]" /> Markets
        </h3>
      </div>
      <div className="divide-y divide-white/5 max-h-[500px] overflow-y-auto">
        {loading ? (
          <div className="p-8 text-center"><Loader2 className="animate-spin text-gray-500 mx-auto" size={20} /></div>
        ) : assets.map(a => (
          <button
            key={a.symbol}
            onClick={() => onSelect(a)}
            className={`w-full flex items-center justify-between p-3 hover:bg-white/5 transition-colors text-left ${
              selectedAsset?.symbol === a.symbol ? 'bg-white/5 border-l-2 border-[#C5A059]' : ''
            }`}
            data-testid={`asset-${a.symbol}`}
          >
            <div className="flex items-center gap-3">
              <span className="text-lg">{a.icon}</span>
              <div>
                <div className="text-white text-sm font-medium">{a.symbol}</div>
                <div className="text-gray-500 text-xs">{a.name}</div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-white text-sm font-mono">{formatUSD(a.price)}</div>
              <PctBadge value={a.change_24h} />
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}


// ---- Trade Form ----
function TradeForm({ selectedAsset, portfolio, onTrade, executing }) {
  const [side, setSide] = useState('buy');
  const [quantity, setQuantity] = useState('');
  const [usdAmount, setUsdAmount] = useState('');
  const [inputMode, setInputMode] = useState('usd'); // 'usd' or 'qty'

  useEffect(() => {
    setQuantity('');
    setUsdAmount('');
  }, [selectedAsset?.symbol]);

  if (!selectedAsset) {
    return (
      <div className="bg-[#111111] border border-white/10 rounded-lg p-8 text-center">
        <LineChart size={32} className="text-gray-600 mx-auto mb-3" />
        <p className="text-gray-400 text-sm">Select an asset to trade</p>
      </div>
    );
  }

  const price = selectedAsset.price;
  const currentHolding = portfolio?.holdings_detail?.[selectedAsset.symbol]?.quantity || 0;

  const handleUsdChange = (val) => {
    setUsdAmount(val);
    setInputMode('usd');
    if (val && price > 0) {
      setQuantity((parseFloat(val) / price).toFixed(8));
    } else {
      setQuantity('');
    }
  };

  const handleQtyChange = (val) => {
    setQuantity(val);
    setInputMode('qty');
    if (val && price > 0) {
      setUsdAmount((parseFloat(val) * price).toFixed(2));
    } else {
      setUsdAmount('');
    }
  };

  const handleSubmit = () => {
    const qty = parseFloat(quantity);
    if (!qty || qty <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    onTrade({ symbol: selectedAsset.symbol, side, quantity: qty });
  };

  const setPercentage = (pct) => {
    if (side === 'buy') {
      const maxUsd = (portfolio?.cash || 0) * pct;
      handleUsdChange(maxUsd.toFixed(2));
    } else {
      const maxQty = currentHolding * pct;
      handleQtyChange(maxQty.toFixed(8));
    }
  };

  return (
    <div className="bg-[#111111] border border-white/10 rounded-lg" data-testid="trade-form">
      <div className="p-4 border-b border-white/10">
        <div className="flex items-center justify-between">
          <h3 className="font-manrope font-semibold text-white text-sm">
            {selectedAsset.icon} {selectedAsset.symbol}/USD
          </h3>
          <span className="text-white font-mono text-sm">{formatUSD(price)}</span>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Buy/Sell toggle */}
        <div className="grid grid-cols-2 gap-1 bg-black/30 p-1 rounded-sm">
          <button
            onClick={() => setSide('buy')}
            className={`py-2 text-sm font-semibold rounded-sm transition-colors ${
              side === 'buy' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'text-gray-400 hover:text-white'
            }`}
            data-testid="trade-buy-btn"
          >
            Buy
          </button>
          <button
            onClick={() => setSide('sell')}
            className={`py-2 text-sm font-semibold rounded-sm transition-colors ${
              side === 'sell' ? 'bg-red-500/20 text-red-400 border border-red-500/30' : 'text-gray-400 hover:text-white'
            }`}
            data-testid="trade-sell-btn"
          >
            Sell
          </button>
        </div>

        {/* Amount input */}
        <div>
          <label className="text-xs text-gray-500 mb-1 block">Amount (USD)</label>
          <input
            type="number"
            value={usdAmount}
            onChange={e => handleUsdChange(e.target.value)}
            placeholder="0.00"
            className="w-full bg-black/50 border border-white/10 rounded-sm px-3 py-2.5 text-white text-sm font-mono focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059]"
            data-testid="trade-usd-input"
          />
        </div>

        <div>
          <label className="text-xs text-gray-500 mb-1 block">Quantity ({selectedAsset.symbol})</label>
          <input
            type="number"
            value={quantity}
            onChange={e => handleQtyChange(e.target.value)}
            placeholder="0.00000000"
            className="w-full bg-black/50 border border-white/10 rounded-sm px-3 py-2.5 text-white text-sm font-mono focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059]"
            data-testid="trade-qty-input"
          />
        </div>

        {/* Quick percentages */}
        <div className="flex gap-2">
          {[0.25, 0.5, 0.75, 1].map(pct => (
            <button
              key={pct}
              onClick={() => setPercentage(pct)}
              className="flex-1 py-1.5 text-xs text-gray-400 bg-white/5 rounded-sm hover:bg-white/10 transition-colors"
            >
              {pct * 100}%
            </button>
          ))}
        </div>

        {/* Info line */}
        <div className="flex justify-between text-xs text-gray-500">
          <span>Available: {side === 'buy' ? formatUSD(portfolio?.cash) : `${currentHolding.toFixed(6)} ${selectedAsset.symbol}`}</span>
          <span>Total: {formatUSD(parseFloat(usdAmount) || 0)}</span>
        </div>

        {/* Execute button */}
        <Button
          onClick={handleSubmit}
          disabled={executing || !quantity || parseFloat(quantity) <= 0}
          className={`w-full rounded-sm py-3 font-semibold text-sm ${
            side === 'buy'
              ? 'bg-emerald-500 hover:bg-emerald-600 text-white'
              : 'bg-red-500 hover:bg-red-600 text-white'
          }`}
          data-testid="trade-execute-btn"
        >
          {executing ? <Loader2 className="animate-spin mr-2" size={14} /> : null}
          {side === 'buy' ? 'Buy' : 'Sell'} {selectedAsset.symbol}
        </Button>
      </div>
    </div>
  );
}


// ---- Portfolio Summary ----
function PortfolioSummary({ portfolio, onReset }) {
  if (!portfolio) return null;

  const returnPositive = portfolio.total_return >= 0;

  return (
    <div className="bg-[#111111] border border-white/10 rounded-lg" data-testid="portfolio-summary">
      <div className="p-4 border-b border-white/10 flex items-center justify-between">
        <h3 className="font-manrope font-semibold text-white text-sm flex items-center gap-2">
          <Wallet size={14} className="text-[#C5A059]" /> Portfolio
        </h3>
        <button
          onClick={onReset}
          className="text-xs text-gray-500 hover:text-red-400 transition-colors flex items-center gap-1"
          data-testid="portfolio-reset-btn"
        >
          <RotateCcw size={12} /> Reset
        </button>
      </div>

      <div className="p-4 space-y-3">
        <div>
          <div className="text-xs text-gray-500 mb-0.5">Total Value</div>
          <div className="text-2xl font-manrope font-bold text-white">{formatUSD(portfolio.total_value)}</div>
          <div className={`text-sm font-medium ${returnPositive ? 'text-emerald-400' : 'text-red-400'}`}>
            {returnPositive ? '+' : ''}{formatUSD(portfolio.total_return)} ({returnPositive ? '+' : ''}{portfolio.total_return_pct?.toFixed(2)}%)
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 pt-2 border-t border-white/5">
          <div>
            <div className="text-xs text-gray-500">Cash</div>
            <div className="text-sm text-white font-mono">{formatUSD(portfolio.cash)}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Holdings</div>
            <div className="text-sm text-white font-mono">{formatUSD(portfolio.holdings_value)}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Trades</div>
            <div className="text-sm text-white">{portfolio.total_trades}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">XP</div>
            <div className="text-sm text-[#C5A059] font-medium">{portfolio.xp}</div>
          </div>
        </div>

        {/* Holdings list */}
        {portfolio.holdings_detail && Object.keys(portfolio.holdings_detail).length > 0 && (
          <div className="pt-3 border-t border-white/5">
            <div className="text-xs text-gray-500 mb-2">Holdings</div>
            <div className="space-y-2">
              {Object.entries(portfolio.holdings_detail).map(([sym, h]) => (
                <div key={sym} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm">{h.icon}</span>
                    <span className="text-sm text-white">{sym}</span>
                    <span className="text-xs text-gray-500">{h.quantity.toFixed(6)}</span>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-white font-mono">{formatUSD(h.value)}</div>
                    <PctBadge value={h.change_24h} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


// ---- Trade History ----
function TradeHistory({ trades, loading }) {
  if (loading) return <div className="p-8 text-center"><Loader2 className="animate-spin text-gray-500 mx-auto" size={20} /></div>;
  if (!trades?.length) return (
    <div className="text-center py-8">
      <History size={32} className="text-gray-600 mx-auto mb-2" />
      <p className="text-gray-500 text-sm">No trades yet</p>
    </div>
  );

  return (
    <div className="overflow-x-auto" data-testid="trade-history">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-gray-500 text-xs border-b border-white/5">
            <th className="text-left py-2 px-3 font-medium">Asset</th>
            <th className="text-left py-2 px-3 font-medium">Side</th>
            <th className="text-right py-2 px-3 font-medium">Qty</th>
            <th className="text-right py-2 px-3 font-medium">Price</th>
            <th className="text-right py-2 px-3 font-medium">Total</th>
            <th className="text-right py-2 px-3 font-medium">Time</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-white/5">
          {trades.map(t => (
            <tr key={t.trade_id} className="hover:bg-white/5">
              <td className="py-2 px-3 text-white font-medium">{t.symbol}</td>
              <td className="py-2 px-3">
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                  t.side === 'buy' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'
                }`}>
                  {t.side.toUpperCase()}
                </span>
              </td>
              <td className="py-2 px-3 text-right text-gray-300 font-mono">{t.quantity.toFixed(6)}</td>
              <td className="py-2 px-3 text-right text-gray-300 font-mono">{formatUSD(t.price)}</td>
              <td className="py-2 px-3 text-right text-white font-mono">{formatUSD(t.total_value)}</td>
              <td className="py-2 px-3 text-right text-gray-500 text-xs">
                {new Date(t.executed_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


// ---- Main Page ----
export default function TradingArena() {
  const { isAuthenticated, login, getToken } = useAuth();
  const [assets, setAssets] = useState([]);
  const [assetsLoading, setAssetsLoading] = useState(true);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [portfolio, setPortfolio] = useState(null);
  const [trades, setTrades] = useState([]);
  const [tradesLoading, setTradesLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [activeTab, setActiveTab] = useState('trade'); // 'trade' | 'history' | 'leaderboard'
  const [leaderboard, setLeaderboard] = useState([]);
  const [lbLoading, setLbLoading] = useState(false);

  const authHeaders = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

  const fetchAssets = useCallback(async () => {
    setAssetsLoading(true);
    try {
      const { data } = await axios.get(`${API}/trading/assets`);
      setAssets(data.assets);
      if (!selectedAsset && data.assets.length > 0) {
        setSelectedAsset(data.assets[0]);
      }
    } catch (err) { console.error(err); }
    setAssetsLoading(false);
  }, []);

  const fetchPortfolio = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const { data } = await axios.get(`${API}/trading/portfolio`, authHeaders());
      setPortfolio(data);
    } catch (err) { console.error(err); }
  }, [isAuthenticated]);

  const fetchTrades = useCallback(async () => {
    if (!isAuthenticated) return;
    setTradesLoading(true);
    try {
      const { data } = await axios.get(`${API}/trading/history?limit=50`, authHeaders());
      setTrades(data.items);
    } catch (err) { console.error(err); }
    setTradesLoading(false);
  }, [isAuthenticated]);

  const fetchLeaderboard = useCallback(async () => {
    setLbLoading(true);
    try {
      const { data } = await axios.get(`${API}/trading/leaderboard`);
      setLeaderboard(data.leaderboard);
    } catch (err) { console.error(err); }
    setLbLoading(false);
  }, []);

  useEffect(() => { fetchAssets(); }, [fetchAssets]);
  useEffect(() => { fetchPortfolio(); }, [fetchPortfolio]);
  useEffect(() => {
    if (activeTab === 'history') fetchTrades();
    if (activeTab === 'leaderboard') fetchLeaderboard();
  }, [activeTab, fetchTrades, fetchLeaderboard]);

  const handleTrade = async ({ symbol, side, quantity }) => {
    setExecuting(true);
    try {
      const { data } = await axios.post(`${API}/trading/execute`, { symbol, side, quantity }, authHeaders());
      toast.success(`${side === 'buy' ? 'Bought' : 'Sold'} ${quantity.toFixed(6)} ${symbol} at ${formatUSD(data.price)} (+${data.xp_gained} XP)`);
      fetchPortfolio();
      fetchTrades();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Trade failed');
    }
    setExecuting(false);
  };

  const handleReset = async () => {
    if (!window.confirm('Reset your portfolio to $100,000? All trade history will be cleared.')) return;
    try {
      await axios.post(`${API}/trading/reset`, { confirm: true }, authHeaders());
      toast.success('Portfolio reset to $100,000');
      fetchPortfolio();
      fetchTrades();
    } catch (err) {
      toast.error('Reset failed');
    }
  };

  // Unauthenticated view
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#0A0A0A]">
        <Navbar />
        <div className="pt-32 pb-20">
          <div className="max-w-2xl mx-auto px-6 text-center">
            <div className="w-16 h-16 bg-[#C5A059]/10 border border-[#C5A059]/30 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <LineChart size={32} className="text-[#C5A059]" />
            </div>
            <h1 className="font-manrope font-bold text-4xl sm:text-5xl text-white mb-4" data-testid="trading-arena-title">
              Demo Trading Arena
            </h1>
            <p className="text-gray-400 text-lg mb-8">
              Practice trading with $100,000 in virtual capital. Trade crypto with real-time market prices, compete on the leaderboard, and build your skills risk-free.
            </p>
            <Button
              onClick={login}
              className="bg-[#C5A059] text-black hover:bg-[#D4AF37] rounded-sm px-8 py-3 font-semibold uppercase text-sm shadow-[0_0_15px_rgba(197,160,89,0.3)] hover:shadow-[0_0_25px_rgba(197,160,89,0.5)]"
              data-testid="trading-login-btn"
            >
              Sign In to Start Trading
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />
      <div className="pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-8 gap-4">
            <div>
              <h1 className="font-manrope font-bold text-3xl sm:text-4xl text-white flex items-center gap-3" data-testid="trading-arena-title">
                <LineChart size={28} className="text-[#C5A059]" />
                Demo Trading Arena
              </h1>
              <p className="text-gray-400 text-sm mt-1">Paper trade with live market prices — $100K virtual capital</p>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={fetchAssets}
                variant="outline"
                className="border-white/20 text-gray-400 hover:text-white hover:bg-white/5 rounded-sm gap-2 text-xs"
                data-testid="refresh-prices-btn"
              >
                <RefreshCw size={12} /> Refresh Prices
              </Button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mb-6 bg-[#111111] border border-white/10 rounded-lg p-1 w-fit" data-testid="arena-tabs">
            {[
              { id: 'trade', label: 'Trade', icon: LineChart },
              { id: 'history', label: 'History', icon: History },
              { id: 'leaderboard', label: 'Leaderboard', icon: Trophy },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-sm text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'bg-[#C5A059]/10 text-[#C5A059] border border-[#C5A059]/30'
                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon size={14} />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Trade Tab */}
          {activeTab === 'trade' && (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              {/* Assets list */}
              <div className="lg:col-span-3">
                <AssetPanel assets={assets} selectedAsset={selectedAsset} onSelect={setSelectedAsset} loading={assetsLoading} />
              </div>

              {/* Trade form */}
              <div className="lg:col-span-4">
                <TradeForm selectedAsset={selectedAsset} portfolio={portfolio} onTrade={handleTrade} executing={executing} />
              </div>

              {/* Portfolio */}
              <div className="lg:col-span-5">
                <PortfolioSummary portfolio={portfolio} onReset={handleReset} />
              </div>
            </div>
          )}

          {/* History Tab */}
          {activeTab === 'history' && (
            <div className="bg-[#111111] border border-white/10 rounded-lg">
              <div className="p-4 border-b border-white/10">
                <h3 className="font-manrope font-semibold text-white flex items-center gap-2">
                  <History size={16} className="text-[#C5A059]" /> Trade History
                </h3>
              </div>
              <TradeHistory trades={trades} loading={tradesLoading} />
            </div>
          )}

          {/* Leaderboard Tab */}
          {activeTab === 'leaderboard' && (
            <div className="bg-[#111111] border border-white/10 rounded-lg" data-testid="leaderboard-panel">
              <div className="p-4 border-b border-white/10 flex items-center justify-between">
                <h3 className="font-manrope font-semibold text-white flex items-center gap-2">
                  <Trophy size={16} className="text-[#C5A059]" /> Leaderboard
                </h3>
                <span className="text-xs text-gray-500">Ranked by portfolio value</span>
              </div>
              {lbLoading ? (
                <div className="p-8 text-center"><Loader2 className="animate-spin text-gray-500 mx-auto" size={20} /></div>
              ) : leaderboard.length === 0 ? (
                <div className="p-8 text-center text-gray-500">No traders yet. Be the first!</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-gray-500 text-xs border-b border-white/5">
                        <th className="text-left py-3 px-4 font-medium">#</th>
                        <th className="text-left py-3 px-4 font-medium">Trader</th>
                        <th className="text-right py-3 px-4 font-medium">Portfolio Value</th>
                        <th className="text-right py-3 px-4 font-medium">Return</th>
                        <th className="text-right py-3 px-4 font-medium">Trades</th>
                        <th className="text-right py-3 px-4 font-medium">Win Rate</th>
                        <th className="text-right py-3 px-4 font-medium">XP</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                      {leaderboard.map((trader, idx) => (
                        <tr key={trader.email} className="hover:bg-white/5">
                          <td className="py-3 px-4">
                            <span className={`font-bold ${idx === 0 ? 'text-[#C5A059]' : idx === 1 ? 'text-gray-300' : idx === 2 ? 'text-amber-700' : 'text-gray-500'}`}>
                              {idx + 1}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              {trader.picture ? (
                                <img src={trader.picture} alt="" className="w-6 h-6 rounded-full" />
                              ) : (
                                <div className="w-6 h-6 rounded-full bg-[#C5A059]/20 flex items-center justify-center text-xs text-[#C5A059] font-bold">
                                  {trader.username?.charAt(0)?.toUpperCase() || '?'}
                                </div>
                              )}
                              <span className="text-white font-medium">{trader.username}</span>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-right text-white font-mono font-medium">{formatUSD(trader.total_value)}</td>
                          <td className="py-3 px-4 text-right">
                            <PctBadge value={trader.total_return_pct} />
                          </td>
                          <td className="py-3 px-4 text-right text-gray-300">{trader.total_trades}</td>
                          <td className="py-3 px-4 text-right text-gray-300">{trader.win_rate}%</td>
                          <td className="py-3 px-4 text-right text-[#C5A059] font-medium">{trader.xp}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
